import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import NotificationsBell from "@/components/forum/NotificationsBell";
import SystemNotificationsBell from "@/components/SystemNotificationsBell";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import WinStreakNotifier from "@/components/WinStreakNotifier";
import CommandPalette from "@/components/CommandPalette";
import WhatsNewModal from "@/components/WhatsNewModal";
import WhatsNewDrawer from "@/components/WhatsNewDrawer";
import UpgradeToProModal from "@/components/UpgradeToProModal";
import SupportWidget from "@/components/SupportWidget";
import VipTrialBanner from "@/components/VipTrialBanner";
import TrialBanner from "@/components/TrialBanner";
import SkipTraceQuotaBadge from "@/components/SkipTraceQuotaBadge";
import AccountMenu from "@/components/AccountMenu";
import { useOwnsBundle } from "@/lib/useOwnsBundle";
import { usePowerDialerEnabled } from "@/lib/usePowerDialerEnabled";
import {
  Building2,
  LayoutDashboard,
  Users,
  Users2,
  KanbanSquare,
  Calculator,
  UserSquare,
  CheckSquare,
  Settings,
  LogOut,
  Inbox as InboxIcon,
  PiggyBank,
  LineChart,
  BarChart3,
  UserPlus,
  Mail,
  DollarSign,
  Trophy,
  Flame,
  Gift,
  BadgeDollarSign,
  Gauge,
  Map as MapIcon,
  Bell,
  CreditCard,
  ShieldCheck,
  FileSignature,
  ChevronDown,
  ChevronRight,
  Wallet,
  DoorOpen,
  Handshake,
  Search,
  Layers,
  BookOpen,
  Library,
  Terminal,
  MessageSquare,
  Mic,
  PhoneCall,
  Menu,
  Zap,
  ShoppingBag,
  Megaphone,
  MailCheck,
  X,
} from "lucide-react";

// Top-level items always visible (not inside a group).
const ESSENTIALS = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/inbox", label: "Inbox", icon: InboxIcon, id: "inbox", showBadge: true },
  { to: "/app/alerts", label: "Alerts", icon: Bell, id: "alerts", showAlertBadge: true },
];

// Community links — rendered after all the collapsible groups. Community
// (Deal Wall) and Forum sit on their own as flat top-level items instead
// of nesting under a "Community" parent, so each shows up as a first-class
// destination in the sidebar with its own icon.
const COMMUNITY_LINKS = [
  { to: "/app/community", label: "Community", icon: Users2, id: "community", end: true },
  { to: "/app/community/forum", label: "Forum", icon: MessageSquare, id: "community-forum", showForumBadge: true },
];

// Collapsible groups. Order is the order shown.
// A group can opt out of collapsibility with `collapsible: false` — the
// label still renders as a section heading, but there's no chevron and
// the items are always visible (used for Core Workflow, Tracy's must-see
// operator loop).
const NAV_GROUPS = [
  {
    id: "core-workflow",
    label: "Core Workflow",
    collapsible: false,
    items: [
      { to: "/app/discover", label: "Discover", icon: MapIcon, id: "discover" },
      { to: "/app/leads", label: "Leads", icon: Users, id: "leads", showFollowUpBadge: true },
      { to: "/app/pipeline", label: "Pipeline", icon: KanbanSquare, id: "pipeline" },
      { to: "/app/buyers", label: "Buyers", icon: Wallet, id: "buyers" },
      { to: "/app/closings", label: "Closings", icon: Handshake, id: "closings", badge: "NEW" },
    ],
  },
  {
    id: "deal-flow",
    label: "Deal Flow",
    items: [
      { to: "/app/marketplace", label: "Lead Marketplace", icon: ShoppingBag, id: "marketplace" },
      { to: "/app/captures", label: "Captures", icon: UserPlus, id: "captures", showCaptureBadge: true },
      { to: "/app/dispo", label: "Deal Rooms", icon: DoorOpen, id: "dispo-rooms", badge: "NEW" },
      { to: "/app/dispo-buyers", label: "Buyer List", icon: Bell, id: "dispo-buyers", badge: "NEW" },
      { to: "/app/heatmap", label: "Deal Heatmap", icon: Layers, id: "heatmap" },
    ],
  },
  {
    id: "underwriting",
    label: "Underwriting",
    items: [
      { to: "/app/analyzer", label: "Analyzer", icon: Calculator, id: "analyzer" },
      { to: "/app/markets", label: "Markets", icon: LineChart, id: "markets" },
      { to: "/app/portfolio", label: "Portfolio", icon: PiggyBank, id: "portfolio" },
      { to: "/app/contracts", label: "Contracts", icon: FileSignature, id: "contracts" },
    ],
  },
  {
    id: "outreach",
    label: "Outreach",
    items: [
      { to: "/app/team", label: "Power Team", icon: UserSquare, id: "team" },
      { to: "/app/apex", label: "Apex AI Voice", icon: PhoneCall, id: "apex" },
      { to: "/app/workspace", label: "Workspace Seats", icon: Users, id: "workspace", requiresTeams: true },
      { to: "/app/recordings", label: "Call Recordings", icon: Mic, id: "recordings" },
      { to: "/app/drip-library", label: "Drip Library", icon: Mail, id: "drip-library", badge: "NEW" },
      { to: "/app/drips", label: "My Drips", icon: MailCheck, id: "drips" },
      { to: "/app/sequences", label: "Sequences", icon: Zap, id: "sequences" },
      { to: "/app/tasks", label: "Tasks", icon: CheckSquare, id: "tasks" },
      { to: "/app/marketing", label: "Marketing", icon: DollarSign, id: "marketing" },
    ],
  },
  {
    id: "growth",
    label: "Growth",
    items: [
      { to: "/app/command-center", label: "Command Center", icon: Gauge, id: "command-center", badge: "NEW" },
      { to: "/app/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
      { to: "/app/wins", label: "Wins", icon: Trophy, id: "wins" },
      { to: "/app/leaderboard", label: "Leaderboard", icon: Flame, id: "leaderboard" },
      { to: "/app/referrals", label: "Refer & Earn", icon: Gift, id: "referrals" },
      { to: "/app/affiliate", label: "Affiliate Program", icon: BadgeDollarSign, id: "affiliate" },
      { to: "/app/marketing-scripts", label: "Marketing Scripts", icon: Megaphone, id: "marketing-scripts", adminOnly: true, badge: "NEW" },
    ],
  },
  {
    id: "learn",
    label: "Learn",
    items: [
      { to: "/app/resources", label: "Resources", icon: BookOpen, id: "resources" },
      { to: "/app/api-docs", label: "API Docs", icon: Terminal, id: "api-docs" },
    ],
  },
];

const GROUP_STATE_KEY = "af_nav_group_open_v1";

function loadGroupState() {
  // Every group defaults to open on first visit. Also used to fill in any
  // groups that didn't exist yet last time the user saved state — so when
  // we add a new nav group (like the Core Workflow split), returning users
  // still see it expanded, not silently collapsed.
  const defaults = Object.fromEntries(NAV_GROUPS.map((g) => [g.id, true]));
  try {
    const raw = localStorage.getItem(GROUP_STATE_KEY);
    if (raw) return { ...defaults, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaults;
}

function saveGroupState(state) {
  try { localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(state)); }
  catch { /* ignore */ }
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [pending, setPending] = useState(0);
  const [captureUnread, setCaptureUnread] = useState(0);
  const [alertsUnread, setAlertsUnread] = useState(0);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [forumUnanswered, setForumUnanswered] = useState(0);
  const [groupOpen, setGroupOpen] = useState(loadGroupState);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Bundle owners must never see the "Free Ebook Library" nav item —
  // they already paid $47 for these books. Swap it to their paid library.
  const { ownsBundle } = useOwnsBundle();
  // Platform flag governs whether Power Dialer nav shows at all — the
  // admin kill switch. Off = the item disappears for everyone.
  const powerDialerOn = usePowerDialerEnabled();
  // Auto-close the drawer whenever the user navigates to a new route.
  useEffect(() => { setMobileNavOpen(false); }, [loc.pathname]);

  const toggleGroup = (id) => {
    setGroupOpen((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveGroupState(next);
      return next;
    });
  };

  // Derive the effective open state at render time so we always force-open
  // the group that contains the active route (e.g. on a fresh page load via
  // direct URL). No effect / setState-in-render needed.
  const effectiveOpen = useMemo(() => {
    const result = { ...groupOpen };
    for (const g of NAV_GROUPS) {
      if (g.items.some((i) => {
        if (loc.pathname === i.to || loc.pathname.startsWith(i.to + "/")) return true;
        return (i.children || []).some((c) =>
          loc.pathname === c.to || loc.pathname.startsWith(c.to + "/"));
      })) {
        result[g.id] = true;
      }
    }
    // "My Library" (bundle owners) lives in the Learn group but is added
    // dynamically, so force-open Learn when the paid library route is active.
    if (loc.pathname.startsWith("/app/paid-library")) {
      result.learn = true;
    }
    return result;
  }, [groupOpen, loc.pathname]);

  // Per-group badge totals so collapsed groups still surface unread counts.
  const groupBadges = useMemo(() => {
    const totals = {};
    for (const g of NAV_GROUPS) {
      let sum = 0;
      for (const it of g.items) {
        if (it.showBadge) sum += unread;
        if (it.showCaptureBadge) sum += captureUnread;
        if (it.showAlertBadge) sum += alertsUnread;
        if (it.showFollowUpBadge) sum += followUpCount;
        for (const c of (it.children || [])) {
          if (c.showForumBadge) sum += forumUnanswered;
        }
      }
      totals[g.id] = sum;
    }
    return totals;
  }, [unread, captureUnread, alertsUnread, followUpCount, forumUnanswered]);

  // eBooks are paid-only now. Show "My Library" in the Learn group ONLY
  // when the user has purchased the $47 Wholesaling Library bundle.
  // Non-owners see no library link at all — no more "free" perception.
  // Power Dialer is injected into Outreach only when the platform flag
  // is on so the sidebar never advertises a dialer the admin has killed.
  const displayedNavGroups = useMemo(() => {
    return NAV_GROUPS.map((g) => {
      if (g.id === "learn" && ownsBundle) {
        return {
          ...g,
          items: [
            ...g.items,
            {
              to: "/app/paid-library",
              label: "My Library",
              icon: Library,
              id: "paid-library-link",
            },
          ],
        };
      }
      if (g.id === "outreach" && powerDialerOn) {
        return {
          ...g,
          items: [
            {
              to: "/app/power-dialer",
              label: "Power Dialer",
              icon: PhoneCall,
              id: "power-dialer",
              badge: "NEW",
            },
            ...g.items,
          ],
        };
      }
      return g;
    });
  }, [ownsBundle, powerDialerOn]);

  const refreshBadge = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox/unread-count");
      setUnread(data.unread_go || 0);
      setPending(data.pending || 0);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/captures/summary");
      setCaptureUnread(data.unread || 0);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/alerts/summary");
      setAlertsUnread(data.unread || 0);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/leads/follow-up-count");
      setFollowUpCount(data.count || 0);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/forum/unanswered-count");
      setForumUnanswered(data.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshBadge();
    const id = setInterval(refreshBadge, 15000);
    const listener = () => refreshBadge();
    window.addEventListener("af:inbox-changed", listener);
    window.addEventListener("af:alerts-changed", listener);
    window.addEventListener("af:followup-changed", listener);
    window.addEventListener("af:forum-changed", listener);
    return () => {
      clearInterval(id);
      window.removeEventListener("af:inbox-changed", listener);
      window.removeEventListener("af:alerts-changed", listener);
      window.removeEventListener("af:followup-changed", listener);
      window.removeEventListener("af:forum-changed", listener);
    };
  }, [refreshBadge]);

  return (
    <div className={`app-shell app-shell-exos${mobileNavOpen ? " mobile-nav-open" : ""}`}>
      <button
        type="button"
        className="mobile-nav-trigger"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation"
        data-testid="mobile-nav-trigger"
      >
        <Menu size={18} />
      </button>
      {mobileNavOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      )}
      <aside className="sidebar" data-testid="app-sidebar">
        <button
          type="button"
          className="mobile-nav-close"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
          data-testid="mobile-nav-close"
        >
          <X size={16} />
        </button>
        <div className="sidebar-brand">
          <span
            className="inline-flex items-center px-2.5 py-2 rounded-xl shadow-lg ring-1 ring-amber-200/40"
            style={{
              backgroundColor: "#FDF8ED",
              backgroundImage: "linear-gradient(135deg, #FEFCF3 0%, #FDF3D8 100%)",
            }}
          >
            <img src="/brand/logo-full-color.png?v=1" alt="iSpeedToClose" className="h-10 md:h-11 w-auto" />
          </span>
        </div>
        <nav className="flex flex-col gap-1" data-testid="sidebar-nav">
          {ESSENTIALS.map((n) => (
            <NavItem
              key={n.to}
              item={n}
              unread={unread}
              pending={pending}
              captureUnread={captureUnread}
              alertsUnread={alertsUnread}
              followUpCount={followUpCount}
            />
          ))}

          {displayedNavGroups.map((g) => {
            // Non-collapsible groups (e.g. Core Workflow) render the label
            // as a static heading with no chevron; items are always shown.
            const isCollapsible = g.collapsible !== false;
            const isOpen = !isCollapsible || !!effectiveOpen[g.id];
            const totalBadge = groupBadges[g.id];
            return (
              <div key={g.id} className="nav-group" data-testid={`nav-group-${g.id}`}>
                {isCollapsible ? (
                  <button
                    type="button"
                    className="nav-group-header"
                    onClick={() => toggleGroup(g.id)}
                    data-testid={`nav-group-toggle-${g.id}`}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <ChevronDown size={12} className="opacity-60" />
                    ) : (
                      <ChevronRight size={12} className="opacity-60" />
                    )}
                    <span className="nav-group-label">{g.label}</span>
                    {!isOpen && totalBadge > 0 && (
                      <span
                        className="badge"
                        style={{ background: "var(--primary)", color: "#fff", fontWeight: 700 }}
                        data-testid={`nav-group-badge-${g.id}`}
                      >
                        {totalBadge}
                      </span>
                    )}
                  </button>
                ) : (
                  <div
                    className="nav-group-header nav-group-header--static"
                    data-testid={`nav-group-static-${g.id}`}
                    aria-hidden="true"
                  >
                    <span className="nav-group-label">{g.label}</span>
                  </div>
                )}
                {isOpen && (
                  <div className="nav-group-items">
                    {g.items
                      .filter((n) => !n.requiresTeams || user?.tier === "team" || !!user?.workspace_id)
                      .filter((n) => !n.adminOnly || user?.role === "admin")
                      .map((n) => (
                      <div key={n.to}>
                        <NavItem
                          item={n}
                          unread={unread}
                          pending={pending}
                          captureUnread={captureUnread}
                          alertsUnread={alertsUnread}
                          followUpCount={followUpCount}
                          forumUnanswered={forumUnanswered}
                        />
                        {(n.children || []).length > 0 && (
                          <div className="ml-5 mt-0.5 mb-1 border-l border-white/10 pl-2 flex flex-col gap-0.5" data-testid={`nav-children-${n.id}`}>
                            {n.children.map((c) => (
                              <NavItem
                                key={c.to}
                                item={c}
                                unread={unread}
                                pending={pending}
                                captureUnread={captureUnread}
                                alertsUnread={alertsUnread}
                                followUpCount={followUpCount}
                                forumUnanswered={forumUnanswered}
                                isChild
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Community + Forum — standalone flat items, not nested under a
              "Community" parent, so each has its own icon + hit target. */}
          {COMMUNITY_LINKS.map((n) => (
            <NavItem
              key={n.to}
              item={n}
              unread={unread}
              pending={pending}
              captureUnread={captureUnread}
              alertsUnread={alertsUnread}
              followUpCount={followUpCount}
              forumUnanswered={forumUnanswered}
            />
          ))}
        </nav>
        <div className="mt-auto">
          <SkipTraceQuotaBadge />
          <div className="divider" style={{ background: "rgba(255,255,255,0.08)" }} />
          <AccountMenu />
        </div>
      </aside>
      <main className="main-shell">
        <ImpersonationBanner />
        <VipTrialBanner />
        <TrialBanner />
        <NotificationsBell />
        <SystemNotificationsBell />
        <WhatsNewDrawer />
        <UpgradeToProModal />
        <Outlet />
        <WinStreakNotifier />
        <WhatsNewModal />
        <SupportWidget />
      </main>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        isAdmin={user?.role === "admin"}
      />
    </div>
  );
}


function NavItem({ item: n, unread, pending, captureUnread, alertsUnread, followUpCount, forumUnanswered, isChild }) {
  return (
    <NavLink
      to={n.to}
      end={n.end}
      className={({ isActive }) =>
        "sidebar-link" + (isActive ? " active" : "") + (isChild ? " sidebar-link-child" : "")
      }
      data-testid={`nav-${n.id}`}
    >
      <n.icon size={isChild ? 14 : 16} className="icon" />
      <span>{n.label}</span>
      {n.showBadge && unread > 0 && (
        <span
          className="badge"
          style={{ background: "var(--primary)", color: "#fff", fontWeight: 700 }}
          data-testid="inbox-unread-badge"
        >
          {unread}
        </span>
      )}
      {n.showBadge && unread === 0 && pending > 0 && (
        <span className="badge" data-testid="inbox-pending-badge">…</span>
      )}
      {n.showCaptureBadge && captureUnread > 0 && (
        <span
          className="badge"
          style={{ background: "#4F46E5", color: "#fff", fontWeight: 700 }}
          data-testid="captures-unread-badge"
        >
          {captureUnread}
        </span>
      )}
      {n.showAlertBadge && alertsUnread > 0 && (
        <span
          className="badge"
          style={{ background: "#F59E0B", color: "#fff", fontWeight: 700 }}
          data-testid="alerts-unread-badge"
        >
          {alertsUnread}
        </span>
      )}
      {n.showFollowUpBadge && followUpCount > 0 && (
        <span
          className="badge"
          style={{ background: "#DC2626", color: "#fff", fontWeight: 700 }}
          title={`${followUpCount} lead${followUpCount === 1 ? "" : "s"} need follow-up`}
          data-testid="leads-followup-badge"
        >
          {followUpCount > 99 ? "99+" : followUpCount}
        </span>
      )}
      {n.showForumBadge && forumUnanswered > 0 && (
        <span
          className="badge"
          style={{ background: "#DC2626", color: "#fff", fontWeight: 700 }}
          title={`${forumUnanswered} forum question${forumUnanswered === 1 ? "" : "s"} unanswered`}
          data-testid="forum-unanswered-badge"
        >
          {forumUnanswered > 99 ? "99+" : forumUnanswered}
        </span>
      )}
      {n.soon && <span className="badge">SOON</span>}
      {n.badge && <span className="badge" style={{background:"#F59E0B",color:"#0B1D3B"}}>{n.badge}</span>}
    </NavLink>
  );
}
